<template>
    <div
        v-if="showBanner"
        class="notification-banner flex flex-col sm:flex-row justify-between sm:justify-center items-center self-stretch bg-red-50 relative z-40 w-full"
        :class="{
            'p-3 sm:p-4 sm:pl-8 sm:pr-16 opacity-100 max-h-[200px] border-b border-red-100':
                visible,
            'p-0 opacity-0 max-h-0 border-none overflow-hidden': !visible,
        }"
        style="
            transition:
                padding 0.3s ease-in-out,
                opacity 0.3s ease-in-out,
                max-height 0.3s ease-in-out,
                border 0.3s ease-in-out;
        "
    >
        <div
            class="flex items-center gap-2 sm:gap-3 pr-2 flex-1 sm:flex-initial sm:mr-8"
        >
            <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="shrink-0"
            >
                <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM8.70711 7.29289C8.31658 6.90237 7.68342 6.90237 7.29289 7.29289C6.90237 7.68342 6.90237 8.31658 7.29289 8.70711L8.58579 10L7.29289 11.2929C6.90237 11.6834 6.90237 12.3166 7.29289 12.7071C7.68342 13.0976 8.31658 13.0976 8.70711 12.7071L10 11.4142L11.2929 12.7071C11.6834 13.0976 12.3166 13.0976 12.7071 12.7071C13.0976 12.3166 13.0976 11.6834 12.7071 11.2929L11.4142 10L12.7071 8.70711C13.0976 8.31658 13.0976 7.68342 12.7071 7.29289C12.3166 6.90237 11.6834 6.90237 11.2929 7.29289L10 8.58579L8.70711 7.29289Z"
                    fill="#EF4444"
                />
            </svg>
            <div
                class="flex flex-col sm:flex-row sm:items-center w-full gap-2 sm:gap-8"
            >
                <div class="text-center sm:text-left">
                    <span
                        class="text-slate-800 font-inter text-sm font-normal leading-5"
                    >
                        {{ bannerMessage }}
                    </span>
                </div>
                <div
                    class="flex justify-center sm:justify-start w-full sm:w-auto"
                >
                    <a
                        :href="
                            requiresAuthentication && paymentUrl
                                ? paymentUrl
                                : '/10xtravel-plus/subscription'
                        "
                        class="mt-2 sm:mt-0 inline-flex items-start gap-2 px-4 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5 sm:ml-2 whitespace-nowrap hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        :target="
                            requiresAuthentication && paymentUrl
                                ? '_blank'
                                : '_self'
                        "
                        @click="handleAction"
                    >
                        {{ actionLabel }}
                    </a>
                </div>
            </div>
        </div>

        <button
            @click="dismiss"
            class="absolute top-2 right-2 sm:hidden p-1 items-center justify-center rounded"
        >
            <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="#64748B"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        </button>

        <button
            @click="dismiss"
            class="hidden sm:flex p-1.5 items-center justify-center rounded absolute right-3 top-2.5"
        >
            <svg
                width="18"
                height="18"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="#64748B"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        </button>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { usePage, router } from '@inertiajs/vue3';
import { useAppStore } from '@/store/app';

const appStore = useAppStore();
const page = usePage();
const visible = ref(false);
const dismissed = ref(false);

const paymentIssue = computed(
    () => page.props.subscription_payment_issue || null
);

const showBanner = computed(() => {
    if (dismissed.value) return false;
    if (!paymentIssue.value) return false;

    const sessionDismissed =
        sessionStorage.getItem('dismiss_payment_issue_banner') === '1';
    if (sessionDismissed) return false;

    return true;
});

const requiresAuthentication = computed(() => {
    if (!paymentIssue.value) return false;
    const reason = paymentIssue.value.reason || '';
    return (
        reason.includes('additional verification') ||
        reason.includes('authentication')
    );
});

const paymentUrl = computed(() => paymentIssue.value?.payment_url || null);

const bannerMessage = computed(() => {
    if (!paymentIssue.value) return '';

    const reason = paymentIssue.value.reason;
    const status = paymentIssue.value.status;

    if (reason) {
        return `Your 10xTravel+ subscription payment failed. ${reason}`;
    }

    switch (status) {
        case 'past_due':
            return 'Your 10xTravel+ subscription payment failed. Update payment method to keep access to premium benefits.';
        case 'incomplete':
            return 'Your 10xTravel+ subscription payment could not be completed. Please update your payment method.';
        case 'unpaid':
            return 'Your 10xTravel+ subscription is unpaid. Update payment method to restore access to premium benefits.';
        default:
            return 'Your 10xTravel+ subscription payment failed. Update payment method to keep access to premium benefits.';
    }
});

const actionLabel = computed(() => {
    if (requiresAuthentication.value && paymentUrl.value) {
        return 'Complete payment';
    }
    return 'View billing';
});

const dismiss = () => {
    visible.value = false;
    setTimeout(() => {
        dismissed.value = true;
        sessionStorage.setItem('dismiss_payment_issue_banner', '1');
    }, 300);
};

const handleAction = (e) => {
    // If it's an external Stripe payment URL, let the native link behavior handle it
    if (requiresAuthentication.value && paymentUrl.value) {
        return; // opens in new tab via native <a> behavior
    }
    // Otherwise use Inertia navigation
    e.preventDefault();
    router.visit('/10xtravel-plus/subscription');
};

watch(visible, (val) => {
    appStore.setPaymentBannerVisible(val);
});

watch(showBanner, (shouldShow) => {
    if (!shouldShow) {
        visible.value = false;
        appStore.setPaymentBannerVisible(false);
        document.body.classList.remove('has-payment-issue');
    }
});

onMounted(() => {
    if (showBanner.value) {
        document.body.classList.add('has-payment-issue');
        setTimeout(() => (visible.value = true), 100);
    }
});

onBeforeUnmount(() => {
    appStore.setPaymentBannerVisible(false);
    document.body.classList.remove('has-payment-issue');
});
</script>
